// btw-front/src/context/PartnerSessionContext.jsx
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { setAccessToken, setOnUnauthenticated } from "../api/client";
import { logout as apiLogout } from "../api/auth";

const KEY_ROLE = "btw.partner.role";
const KEY_PROFILE = "btw.partner.profile";
const KEY_SIGNUP_TOKEN = "btw.partner.signupToken";
const KEY_LAST_INTENT = "btw.partner.lastIntent";

const VALID_ROLES = ["guest", "pending", "partner", "admin"];

// 빈 프로필 — 로그아웃/게스트 기본값
const EMPTY_PROFILE = {
  partnerId: null,
  company: null,
  userId: null,
  name: null,
  email: null,
};

function readStorage(key, fallback) {
  try {
    const v = sessionStorage.getItem(key);
    if (v == null) return fallback;
    return JSON.parse(v);
  } catch (e) {
    return fallback;
  }
}
function writeStorage(key, value) {
  try {
    if (value == null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    /* ignore */
  }
}

const Ctx = createContext(null);

export function PartnerSessionProvider({ children }) {
  const [role, setRoleState] = useState(() => {
    const r = readStorage(KEY_ROLE, "guest");
    return VALID_ROLES.includes(r) ? r : "guest";
  });
  const [profile, setProfileState] = useState(() => readStorage(KEY_PROFILE, EMPTY_PROFILE));
  const [signupToken, setSignupTokenState] = useState(() => readStorage(KEY_SIGNUP_TOKEN, null));
  const [lastIntent, setLastIntentState] = useState(() => readStorage(KEY_LAST_INTENT, null));

  // 역할만 변경 — 프로필은 호출자가 별도로 setProfile / signIn 으로 채운다.
  // (게스트 전환 시엔 logout 이 별도로 프로필을 비움)
  const setRole = useCallback((next) => {
    if (!VALID_ROLES.includes(next)) next = "guest";
    setRoleState(next);
    writeStorage(KEY_ROLE, next);
  }, []);

  const setProfile = useCallback((next) => {
    setProfileState(next);
    writeStorage(KEY_PROFILE, next);
  }, []);

  // 인증 성공 시 — role + 프로필 + accessToken 한 번에 반영.
  const signIn = useCallback((nextRole, nextProfile, accessToken) => {
    if (!VALID_ROLES.includes(nextRole)) nextRole = "guest";
    setRoleState(nextRole);
    writeStorage(KEY_ROLE, nextRole);
    const finalProfile = nextProfile || EMPTY_PROFILE;
    setProfileState(finalProfile);
    writeStorage(KEY_PROFILE, finalProfile);
    if (accessToken !== undefined) setAccessToken(accessToken);
  }, []);

  const setSignupToken = useCallback((next) => {
    setSignupTokenState(next);
    writeStorage(KEY_SIGNUP_TOKEN, next);
  }, []);

  const setLastIntent = useCallback((next) => {
    setLastIntentState(next);
    writeStorage(KEY_LAST_INTENT, next);
  }, []);

  const clearSignupToken = useCallback(() => setSignupToken(null), [setSignupToken]);

  const logout = useCallback(() => {
    apiLogout().catch(() => {});
    setAccessToken(null);
    setRole("guest");
    setProfile(EMPTY_PROFILE);
    setSignupToken(null);
    setLastIntent(null);
  }, [setRole, setProfile, setSignupToken, setLastIntent]);

  // storage 이벤트 동기화 (다른 탭에서 변경 시 — 목업이라 best effort)
  useEffect(() => {
    function onStorage(e) {
      if (e.key === KEY_ROLE) {
        const r = readStorage(KEY_ROLE, "guest");
        if (VALID_ROLES.includes(r)) setRoleState(r);
      } else if (e.key === KEY_PROFILE) {
        setProfileState(readStorage(KEY_PROFILE, EMPTY_PROFILE));
      } else if (e.key === KEY_SIGNUP_TOKEN) {
        setSignupTokenState(readStorage(KEY_SIGNUP_TOKEN, null));
      }
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // 401 응답 시 자동 로그아웃 (api/client.js 에서 호출)
  useEffect(() => {
    setOnUnauthenticated(() => {
      setRole("guest");
      setProfile(EMPTY_PROFILE);
      setSignupToken(null);
      setLastIntent(null);
    });
    return () => setOnUnauthenticated(null);
  }, [setRole, setProfile, setSignupToken, setLastIntent]);

  const value = useMemo(
    () => ({
      role,
      profile,
      signupToken,
      lastIntent,
      setRole,
      setProfile,
      signIn,
      setSignupToken,
      clearSignupToken,
      setLastIntent,
      logout,
    }),
    [
      role,
      profile,
      signupToken,
      lastIntent,
      setRole,
      setProfile,
      signIn,
      setSignupToken,
      clearSignupToken,
      setLastIntent,
      logout,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePartnerSession() {
  const v = useContext(Ctx);
  if (!v) throw new Error("usePartnerSession must be used inside PartnerSessionProvider");
  return v;
}
